import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMailReading } from "@/lib/mail-reading-context";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListMailAccounts,
  useListMailFolders,
  useListMailMessages,
  useGetMailMessage,
  useSyncMailAccount,
  useSyncMailFolder,
  useMarkMailMessageRead,
  useFlagMailMessage,
  useDeleteMailMessage,
  useAnalyzeMailMessage,
  useTriageMailMessage,
  useCorrectMailMessageTriage,
  useListMailMessageSuggestions,
  useAcceptSuggestion,
  useMoveMailMessage,
  useUpdateMailAccount,
  useTestMailAccount,
  getListMailAccountsQueryKey,
  getListMailFoldersQueryKey,
  getListMailMessagesQueryKey,
  getGetMailMessageQueryKey,
  getListMailMessageSuggestionsQueryKey,
  getGetMailUnreadSummaryQueryKey,
  getListSuggestionsQueryKey,
  getListInboxQueryKey,
  getGetMailOldThreadCandidatesSummaryQueryKey,
  getListProjectsQueryKey,
  type MailAccount,
  type MailFolder,
  type MailMessage,
  type MailMessageWithBody,
  type InboxItemWithSuggestions,
  type SuggestedAction,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Mail as MailIcon,
  RefreshCw,
  Inbox as InboxIcon,
  Send as SendIcon,
  Star,
  StarOff,
  Trash2,
  Sparkles,
  Reply,
  PenSquare,
  Archive,
  AlertCircle,
  FileText,
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Search,
  ArrowLeft,
  MoreVertical,
  Loader2,
  Zap,
  Bell,
  HelpCircle,
  Calendar as CalendarIcon,
  X,
  CheckSquare,
  ExternalLink,
  ShieldCheck,
  EyeOff,
  Paperclip,
  Download,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDistanceToNow, format, isToday, isYesterday } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useToast } from "@/hooks/use-toast";
import MailComposer from "@/components/mail-composer";
import DOMPurify from "dompurify";
import {
  TriageBadge,
  triageLabel,
  TRIAGE_CATEGORIES,
  type TriageCategory,
} from "@/components/triage-badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// ─── Folder utilities ─────────────────────────────────────────────────────────

const ROLE_I18N_KEYS: Record<string, string> = {
  inbox: "mail_page.folder_inbox",
  sent: "mail_page.folder_sent",
  drafts: "mail_page.folder_drafts",
  trash: "mail_page.folder_trash",
  archive: "mail_page.folder_archive",
  spam: "mail_page.folder_spam",
};

const ROLE_ORDER = ["inbox", "sent", "drafts", "archive", "spam", "trash", "other"];

function FolderIcon({ role, className = "w-4 h-4" }: { role: string; className?: string }) {
  if (role === "inbox") return <InboxIcon className={className} />;
  if (role === "sent") return <SendIcon className={className} />;
  if (role === "drafts") return <FileText className={className} />;
  if (role === "trash") return <Trash2 className={className} />;
  if (role === "archive") return <Archive className={className} />;
  if (role === "spam") return <AlertCircle className={className} />;
  return <Folder className={className} />;
}

interface FolderNode {
  folder: MailFolder;
  children: FolderNode[];
}

function buildFolderTree(folders: MailFolder[]): FolderNode[] {
  const sorted = [...folders].sort((a, b) => a.path.localeCompare(b.path));
  const map = new Map<string, FolderNode>();
  const roots: FolderNode[] = [];

  for (const f of sorted) {
    const node: FolderNode = { folder: f, children: [] };
    map.set(f.path, node);
    const segments = f.path.split(f.delimiter || "/");
    const parentPath = segments.slice(0, -1).join(f.delimiter || "/");
    const parentNode = map.get(parentPath);
    if (parentNode) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort roots by role order
  roots.sort((a, b) => {
    const ai = ROLE_ORDER.indexOf(a.folder.role);
    const bi = ROLE_ORDER.indexOf(b.folder.role);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi) || a.folder.name.localeCompare(b.folder.name);
  });

  return roots;
}

function folderDisplayName(f: MailFolder, t: (key: string) => string): string {
  if (ROLE_I18N_KEYS[f.role] && !f.path.includes("/")) {
    return t(ROLE_I18N_KEYS[f.role]);
  }
  return f.name;
}

// ─── Date formatting ──────────────────────────────────────────────────────────

function formatMsgDate(raw: string | undefined | null, dateLocale: typeof fr, yesterday: string): string {
  if (!raw) return "";
  const d = new Date(raw);
  if (isToday(d)) return format(d, "HH:mm");
  if (isYesterday(d)) return yesterday;
  return format(d, "d MMM", { locale: dateLocale });
}

// ─── Sender initials ──────────────────────────────────────────────────────────

function senderInitial(name: string | undefined | null, email: string | undefined | null): string {
  const src = name || email || "?";
  return src.charAt(0).toUpperCase();
}

// ─── Empty state ──────────────────────────────────────────────────────────────

// ─── Sync error banner ────────────────────────────────────────────────────────

function MailSyncErrorBanner({
  account,
  onReconnect,
}: {
  account: MailAccount;
  onReconnect: () => void;
}) {
  if (!account.lastError) return null;
  const errMsg =
    account.lastError.length > 140
      ? account.lastError.slice(0, 140) + "…"
      : account.lastError;
  return (
    <div
      className="flex items-start gap-2 px-3 py-2 border-b border-destructive/30 bg-destructive/5 text-destructive shrink-0"
      data-testid="mail-sync-error-banner"
    >
      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0 text-xs leading-snug">
        <p className="font-semibold">{account.label} — erreur de synchronisation</p>
        <p className="text-muted-foreground truncate">{errMsg}</p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onReconnect}
        className="shrink-0 h-7 text-xs border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive"
        data-testid="mail-sync-error-reconnect-btn"
      >
        Reconnecter
      </Button>
    </div>
  );
}

// ─── Reconnect modal ───────────────────────────────────────────────────────────

function ReconnectMailModal({
  account,
  open,
  onOpenChange,
  onReconnected,
}: {
  account: MailAccount;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onReconnected: () => void;
}) {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const testMutation = useTestMailAccount({ mutation: {} });
  const updateMutation = useUpdateMailAccount({
    mutation: {
      onSuccess: () => {
        toast({ title: "Compte reconnecté avec succès" });
        onReconnected();
        onOpenChange(false);
        setPassword("");
      },
      onError: (err: Error) =>
        toast({ title: "Erreur lors de la mise à jour", description: err.message, variant: "destructive" }),
    },
  });

  const isBusy = testMutation.isPending || updateMutation.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!password) {
      toast({ title: "Mot de passe requis", variant: "destructive" });
      return;
    }
    const payload = {
      imapHost: account.imapHost,
      imapPort: account.imapPort,
      imapSecure: account.imapSecure,
      imapUsername: account.imapUsername,
      imapPassword: password,
      smtpHost: account.smtpHost,
      smtpPort: account.smtpPort,
      smtpSecure: account.smtpSecure,
      smtpUsername: account.smtpUsername,
      smtpPassword: password,
    };
    try {
      const result = await testMutation.mutateAsync({ data: { ...payload, label: account.label, emailAddress: account.emailAddress, isDefault: account.isDefault, autoAnalyzeFromContains: account.autoAnalyzeFromContains, autoSync: account.autoSync } });
      if (!result.imap.ok || !result.smtp.ok) {
        const err = result.imap.error || result.smtp.error || "Connexion refusée";
        toast({ title: "Test de connexion échoué", description: err, variant: "destructive" });
        return;
      }
    } catch {
      toast({ title: "Impossible de tester la connexion", variant: "destructive" });
      return;
    }
    updateMutation.mutate({ id: account.id, data: { imapPassword: password, smtpPassword: password } });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MailIcon className="w-4 h-4 text-primary" />
            Reconnecter la boîte mail
          </DialogTitle>
          <DialogDescription>
            Saisissez à nouveau le mot de passe (ou mot de passe d'application) pour{" "}
            <strong>{account.emailAddress}</strong>.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="reconnect-password">Mot de passe</Label>
            <Input
              id="reconnect-password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isBusy}
              autoFocus
              data-testid="input-reconnect-password"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={isBusy}>
              Annuler
            </Button>
            <Button type="submit" size="sm" disabled={isBusy} data-testid="btn-reconnect-submit">
              {isBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {isBusy ? "Vérification…" : "Reconnecter"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Discreet amber banner shown at the top of the message list when the user has
 * exhausted today's AI analysis budget. New mails are still fetched, but they
 * stay un-triaged ("À analyser plus tard") until midnight UTC.
 *
 * The cap is per-user but configured per-account; the API stamps the same
 * `dailyAiUsageRemaining` on every account belonging to the same user, so
 * checking the active account is equivalent to checking the global state.
 */
function DailyAiCapBanner({ account }: { account: MailAccount }) {
  const { t } = useTranslation();
  const remaining = account.dailyAiUsageRemaining ?? 0;
  if (remaining > 0) return null;
  return (
    <div
      className="flex items-start gap-2 px-3 py-2 border-b border-amber-300/40 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200"
      data-testid="banner-ai-cap-reached"
    >
      <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0 text-xs leading-snug">
        <p>
          {t("mail_page.ai_cap_text", {
            used: account.dailyAiUsageToday ?? 0,
            cap: account.dailyAiAnalysisCap,
          })}
        </p>
        <Link
          href="/app/settings"
          className="underline hover:no-underline font-medium"
          data-testid="link-ai-cap-settings"
        >
          {t("mail_page.ai_cap_link")}
        </Link>
      </div>
    </div>
  );
}

function NoAccountState() {
  const { t } = useTranslation();
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="bg-card border border-border rounded-2xl p-10 text-center max-w-sm w-full">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
          <MailIcon className="w-7 h-7" />
        </div>
        <h2 className="font-semibold text-xl mb-2">{t("mail_page.no_account_title")}</h2>
        <p className="text-muted-foreground text-sm mb-6">
          {t("mail_page.no_account_desc")}
        </p>
        <Link href="/app/settings">
          <Button size="lg" data-testid="button-go-settings">{t("mail_page.add_account_btn")}</Button>
        </Link>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function MailPage() {
  const { setMailReading } = useMailReading();

  // Replie la nav dès l'entrée sur la page Mail, la restaure au départ
  useEffect(() => {
    setMailReading(true);
    return () => setMailReading(false);
  }, [setMailReading]);

  const { data: accounts, isLoading } = useListMailAccounts({
    query: { queryKey: getListMailAccountsQueryKey(), refetchInterval: 60_000 },
  });
  const accountList = (accounts ?? []) as MailAccount[];
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);

  useEffect(() => {
    if (!activeAccountId && accountList.length > 0) {
      const def = accountList.find((a) => a.isDefault) ?? accountList[0];
      setActiveAccountId(def.id);
    }
  }, [accountList, activeAccountId]);

  if (isLoading) {
    return (
      <div className="h-full flex">
        <div className="w-56 border-r p-3 space-y-2 hidden md:block">
          {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-8 w-full rounded-lg" />)}
        </div>
        <div className="flex-1 p-3 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (accountList.length === 0) return <NoAccountState />;

  const activeAccount = accountList.find((a) => a.id === activeAccountId) ?? accountList[0];

  return (
    <MailWorkspace
      accounts={accountList}
      activeAccount={activeAccount}
      onSwitchAccount={setActiveAccountId}
    />
  );
}

// ─── Workspace ────────────────────────────────────────────────────────────────

type MailView = "list" | "reader";

function MailWorkspace({
  accounts,
  activeAccount,
  onSwitchAccount,
}: {
  accounts: MailAccount[];
  activeAccount: MailAccount;
  onSwitchAccount: (id: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const { toast } = useToast();
  const qc = useQueryClient();
  const accountId = activeAccount.id;
  const { setMailReading } = useMailReading();

  const { data: folders } = useListMailFolders(accountId, {
    query: { queryKey: getListMailFoldersQueryKey(accountId), refetchInterval: 60_000 },
  });
  const folderList = (folders ?? []) as MailFolder[];
  const folderTree = useMemo(() => buildFolderTree(folderList), [folderList]);

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedFolderId && folderList.length > 0) {
      const inbox = folderList.find((f) => f.role === "inbox") ?? folderList[0];
      setSelectedFolderId(inbox.id);
    }
  }, [folderList, selectedFolderId]);

  const selectedFolder = folderList.find((f) => f.id === selectedFolderId) ?? null;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [openSwipeRowId, setOpenSwipeRowId] = useState<string | null>(null);
  useEffect(() => { setOpenSwipeRowId(null); }, [selectedFolderId]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── IMAP IDLE push via SSE ─────────────────────────────────────────────────
  // Connect to the server-sent events stream.  When IMAP IDLE detects a new
  // message, the server pushes a "mail:update" event and we invalidate the
  // relevant queries so the UI refreshes immediately.
  // The 30–60 s refetchIntervals below act as a safe fallback if SSE drops.
  // We use a ref for selectedFolderId so the effect only reconnects when the
  // account changes (not every time the selected folder changes).
  const selectedFolderIdRef = useRef(selectedFolderId);
  selectedFolderIdRef.current = selectedFolderId;
  const debouncedSearchRef = useRef(debouncedSearch);
  debouncedSearchRef.current = debouncedSearch;

  useEffect(() => {
    const es = new EventSource("/api/mail/events");

    const handleUpdate = (event: MessageEvent<string>) => {
      const payload = JSON.parse(event.data) as { accountId: string };
      const updatedAccountId = payload.accountId;

      // Always refresh the global unread count (powers the nav badge).
      void qc.invalidateQueries({ queryKey: getGetMailUnreadSummaryQueryKey() });

      // Refresh folder counts for the account that received new mail,
      // regardless of which account is currently displayed.
      void qc.invalidateQueries({ queryKey: getListMailFoldersQueryKey(updatedAccountId) });

      // If the update concerns the currently visible account, also refresh
      // the message list for the active folder.
      if (updatedAccountId === accountId) {
        void qc.invalidateQueries({
          queryKey: getListMailMessagesQueryKey(accountId, {
            folderId: selectedFolderIdRef.current ?? undefined,
            q: debouncedSearchRef.current || undefined,
            limit: 100,
          }),
        });
      }
    };

    es.addEventListener("mail:update", handleUpdate);

    return () => {
      es.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const { data: messages, isFetching: messagesFetching } = useListMailMessages(
    accountId,
    { folderId: selectedFolderId ?? undefined, q: debouncedSearch || undefined, limit: 100 },
    {
      query: {
        queryKey: getListMailMessagesQueryKey(accountId, {
          folderId: selectedFolderId ?? undefined,
          q: debouncedSearch || undefined,
          limit: 100,
        }),
        enabled: Boolean(selectedFolderId),
        refetchInterval: 30_000,
      },
    },
  );
  const messageList = (messages ?? []) as MailMessage[];

  const [openMessageId, setOpenMessageId] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<MailView>("list");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const idParam = params.get("id");
    if (idParam) {
      setOpenMessageId(idParam);
      setMobileView("reader");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const [folderSheetOpen, setFolderSheetOpen] = useState(false);
  const [reconnectOpen, setReconnectOpen] = useState(false);
  const [reconnectAccount, setReconnectAccount] = useState<MailAccount | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerInitial, setComposerInitial] = useState<{
    to?: string[];
    subject?: string;
    bodyText?: string;
    inReplyTo?: string | null;
    references?: string | null;
  } | null>(null);

  const syncAccount = useSyncMailAccount({
    mutation: {
      onSuccess: () => {
        toast({ title: t("mail_page.synced_toast") });
        qc.invalidateQueries({ queryKey: getListMailAccountsQueryKey() });
        qc.invalidateQueries({ queryKey: getListMailFoldersQueryKey(accountId) });
        if (selectedFolderId) {
          qc.invalidateQueries({
            queryKey: getListMailMessagesQueryKey(accountId, {
              folderId: selectedFolderId,
              q: debouncedSearch || undefined,
              limit: 100,
            }),
          });
        }
      },
      onError: (err: Error) =>
        toast({ title: t("mail_page.sync_error"), description: err.message, variant: "destructive" }),
    },
  });

  const syncFolder = useSyncMailFolder({
    mutation: {
      onSuccess: () => {
        if (selectedFolderId) {
          qc.invalidateQueries({
            queryKey: getListMailMessagesQueryKey(accountId, {
              folderId: selectedFolderId,
              q: debouncedSearch || undefined,
              limit: 100,
            }),
          });
          qc.invalidateQueries({ queryKey: getListMailFoldersQueryKey(accountId) });
        }
      },
    },
  });

  // ── Swipe-action mutations (used by swipeable MessageRow on mobile) ─────────
  const flagSwipe = useFlagMailMessage();
  const moveSwipe = useMoveMailMessage();
  const markReadSwipe = useMarkMailMessageRead();
  const archiveFolder = folderList.find((f) => f.role === "archive") ?? null;
  const trashFolder = folderList.find((f) => f.role === "trash") ?? null;

  function invalidateListAfterSwipe() {
    if (selectedFolderId) {
      void qc.invalidateQueries({
        queryKey: getListMailMessagesQueryKey(accountId, {
          folderId: selectedFolderId,
          q: debouncedSearch || undefined,
          limit: 100,
        }),
      });
    }
    void qc.invalidateQueries({ queryKey: getListMailFoldersQueryKey(accountId) });
  }

  function selectFolder(id: string) {
    setSelectedFolderId(id);
    setOpenMessageId(null);
    setMobileView("list");
    setFolderSheetOpen(false);
  }

  function openMessage(id: string) {
    setOpenMessageId(id);
    setMobileView("reader");
  }

  function backToList() {
    setOpenMessageId(null);
    setMobileView("list");
  }

  const folderLabel = selectedFolder ? folderDisplayName(selectedFolder, t) : "Mail";

  return (
    <TooltipProvider delayDuration={300}>
    <div className="flex h-full bg-background overflow-hidden">

      {/* ── Col 1 : Sidebar dossiers — toujours visible sur desktop (nav repliée sur toute la page mail) ── */}
      <aside className={`hidden md:flex flex-col h-full shrink-0 bg-background border-r border-border overflow-hidden transition-all duration-300 ${openMessageId ? "w-12" : "w-52"}`}>

        {/* Composer + compte */}
        <div className={`flex flex-col border-b border-border shrink-0 ${openMessageId ? "p-1.5 items-center" : "p-3 space-y-2"}`}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className={openMessageId ? "w-8 h-8 p-0" : "w-full justify-start"}
                size="sm"
                onClick={() => { setComposerInitial(null); setComposerOpen(true); }}
                data-testid="button-new-mail"
              >
                <PenSquare className={`w-4 h-4 ${openMessageId ? "" : "mr-2"}`} />
                {!openMessageId && t("mail_page.new_mail_btn")}
              </Button>
            </TooltipTrigger>
            {openMessageId && <TooltipContent side="right">{t("mail_page.new_mail_btn")}</TooltipContent>}
          </Tooltip>
          {!openMessageId && (
            accounts.length > 1 ? (
              <select
                className="w-full text-xs border border-border rounded-md px-2 py-1.5 bg-background"
                value={accountId}
                onChange={(e) => onSwitchAccount(e.target.value)}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            ) : (
              <p className="text-[11px] text-muted-foreground truncate px-1">{activeAccount.emailAddress}</p>
            )
          )}
        </div>

        {/* Arborescence dossiers */}
        <ScrollArea className="flex-1 min-h-0">
          <nav className="p-2">
            <FolderTree
              nodes={folderTree}
              selectedId={selectedFolderId}
              onSelect={selectFolder}
              depth={0}
              collapsed={!!openMessageId}
            />
          </nav>
        </ScrollArea>

        {/* Sync */}
        <div className={`border-t border-border shrink-0 ${openMessageId ? "p-1.5 flex justify-center" : "p-2"}`}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size={openMessageId ? "icon" : "sm"}
                className={openMessageId ? "w-8 h-8" : "w-full justify-start text-xs"}
                onClick={() => syncAccount.mutate({ id: accountId })}
                disabled={syncAccount.isPending}
                data-testid="button-sync"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${syncAccount.isPending ? "animate-spin" : ""} ${openMessageId ? "" : "mr-2"}`} />
                {!openMessageId && t("mail_page.sync_btn")}
              </Button>
            </TooltipTrigger>
            {openMessageId && <TooltipContent side="right">{t("mail_page.sync_btn")}</TooltipContent>}
          </Tooltip>
          {!openMessageId && activeAccount.lastSyncAt && (
            <p className="text-[10px] text-muted-foreground px-2 mt-1">
              {t("mail_page.updated_time", { time: formatDistanceToNow(new Date(activeAccount.lastSyncAt), { addSuffix: true, locale: dateLocale }) })}
            </p>
          )}
          {!openMessageId && activeAccount.lastError && (
            <p className="text-[10px] text-destructive px-2 mt-1 line-clamp-2">{activeAccount.lastError}</p>
          )}
        </div>
      </aside>

      {/* ── Col 2 : Liste messages — pleine largeur sans mail, étroite avec mail ── */}
      <section className={`${openMessageId ? "hidden md:flex md:w-72 md:shrink-0 border-r border-border" : "flex flex-1"} flex-col bg-background overflow-hidden`}>

        {/* En-tête dossier */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-border shrink-0">
          {/* Mobile: sélecteur dossier */}
          <button
            className="md:hidden flex items-center gap-1 text-sm font-semibold text-foreground shrink-0 max-w-[9rem] truncate"
            onClick={() => setFolderSheetOpen(true)}
          >
            {selectedFolder && <FolderIcon role={selectedFolder.role} className="w-4 h-4 text-primary shrink-0" />}
            <span className="truncate">{folderLabel}</span>
            {selectedFolder && selectedFolder.unreadCount > 0 && (
              <Badge className="ml-1 h-4 text-[10px] px-1">{selectedFolder.unreadCount}</Badge>
            )}
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-0.5 shrink-0" />
          </button>

          {/* Desktop: titre dossier */}
          <div className="hidden md:flex items-center gap-2 min-w-0">
            {selectedFolder && <FolderIcon role={selectedFolder.role} className="w-4 h-4 text-primary shrink-0" />}
            <span className="text-sm font-semibold truncate">{folderLabel}</span>
            {selectedFolder && selectedFolder.unreadCount > 0 && (
              <Badge className="h-4 text-[10px] px-1">{selectedFolder.unreadCount}</Badge>
            )}
          </div>

          <div className="flex-1" />

          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 shrink-0"
            onClick={() => syncFolder.mutate({ id: selectedFolderId! })}
            disabled={syncFolder.isPending || !selectedFolderId}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${syncFolder.isPending ? "animate-spin" : ""}`} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="w-8 h-8 shrink-0 md:hidden"
            onClick={() => { setComposerInitial(null); setComposerOpen(true); }}
          >
            <PenSquare className="w-3.5 h-3.5" />
          </Button>
        </div>

        {/* Recherche */}
        <div className="px-3 py-2 border-b border-border shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("mail_page.search_placeholder")}
              className="pl-8 h-8 text-sm"
              data-testid="input-search-mail"
            />
          </div>
        </div>

        {/* Bannière erreur de sync — cible le compte actif s'il est en erreur,
            sinon le premier compte en erreur parmi tous les comptes. */}
        {(() => {
          const errored = activeAccount.syncStatus === "error"
            ? activeAccount
            : accounts.find((a) => a.syncStatus === "error") ?? null;
          if (!errored) return null;
          return (
            <MailSyncErrorBanner
              account={errored}
              onReconnect={() => {
                setReconnectAccount(errored);
                setReconnectOpen(true);
              }}
            />
          );
        })()}

        {/* Bannière cap IA */}
        <DailyAiCapBanner account={activeAccount} />

        {/* Liste messages */}
        <ScrollArea className="flex-1 min-h-0 [&>div>div]:!block">
          {messagesFetching && messageList.length === 0 ? (
            <div className="p-3 space-y-1.5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex gap-3 p-2">
                  <Skeleton className="w-8 h-8 rounded-full shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : messageList.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground space-y-3">
              <MailIcon className="w-8 h-8 mx-auto opacity-30" />
              <p>{t("mail_page.empty_folder")}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => syncFolder.mutate({ id: selectedFolderId! })}
                disabled={syncFolder.isPending || !selectedFolderId}
              >
                <RefreshCw className="w-3.5 h-3.5 mr-2" /> {t("mail_page.force_sync_btn")}
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-border/60">
              {messageList.map((m) => (
                <MessageRow
                  key={m.id}
                  message={m}
                  active={openMessageId === m.id}
                  onClick={() => openMessage(m.id)}
                  onFlag={() =>
                    flagSwipe.mutate(
                      { id: m.id, data: { isFlagged: !m.isFlagged } },
                      { onSuccess: () => invalidateListAfterSwipe() },
                    )
                  }
                  onArchive={
                    archiveFolder
                      ? () =>
                          moveSwipe.mutate(
                            { id: m.id, data: { folderId: archiveFolder.id } },
                            {
                              onSuccess: () => {
                                toast({ title: t("mail_page.archived_toast") });
                                invalidateListAfterSwipe();
                                if (openMessageId === m.id) backToList();
                              },
                            },
                          )
                      : undefined
                  }
                  onDelete={
                    trashFolder
                      ? () =>
                          moveSwipe.mutate(
                            { id: m.id, data: { folderId: trashFolder.id } },
                            {
                              onSuccess: () => {
                                toast({ title: t("mail_page.moved_to_trash_toast") });
                                invalidateListAfterSwipe();
                                if (openMessageId === m.id) backToList();
                              },
                            },
                          )
                      : undefined
                  }
                  onMarkRead={
                    m.isUnread
                      ? () =>
                          markReadSwipe.mutate(
                            { id: m.id, data: { isRead: true } },
                            { onSuccess: () => invalidateListAfterSwipe() },
                          )
                      : undefined
                  }
                  onMarkUnread={
                    !m.isUnread
                      ? () =>
                          markReadSwipe.mutate(
                            { id: m.id, data: { isRead: false } },
                            { onSuccess: () => invalidateListAfterSwipe() },
                          )
                      : undefined
                  }
                  isRevealedByParent={openSwipeRowId === m.id}
                  onSwipeOpen={() => setOpenSwipeRowId(m.id)}
                />
              ))}
            </ul>
          )}
        </ScrollArea>
      </section>

      {/* ── Col 3 : Volet de lecture — caché si aucun mail sélectionné ── */}
      <section className={`${openMessageId ? "flex" : "hidden"} flex-1 min-w-0 flex-col overflow-hidden bg-background`}>
        {openMessageId ? (
          <MailReader
            messageId={openMessageId}
            account={activeAccount}
            folderList={folderList}
            onBack={backToList}
            onReply={(initial) => { setComposerInitial(initial); setComposerOpen(true); }}
            onDelete={() => {
              backToList();
              if (selectedFolderId) {
                qc.invalidateQueries({
                  queryKey: getListMailMessagesQueryKey(accountId, {
                    folderId: selectedFolderId,
                    q: debouncedSearch || undefined,
                    limit: 100,
                  }),
                });
              }
            }}
            onMoveSuccess={() => {
              backToList();
              if (selectedFolderId) {
                qc.invalidateQueries({
                  queryKey: getListMailMessagesQueryKey(accountId, {
                    folderId: selectedFolderId,
                    q: debouncedSearch || undefined,
                    limit: 100,
                  }),
                });
              }
              qc.invalidateQueries({ queryKey: getListMailFoldersQueryKey(accountId) });
            }}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center text-muted-foreground/40 space-y-3 select-none">
              <MailIcon className="w-12 h-12 mx-auto opacity-20" />
              <p className="text-sm">{t("mail_page.select_message")}</p>
            </div>
          </div>
        )}
      </section>

      {/* ── Mobile folder sheet ── */}
      <Sheet open={folderSheetOpen} onOpenChange={setFolderSheetOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col">
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="text-sm">{activeAccount.emailAddress}</SheetTitle>
          </SheetHeader>
          {accounts.length > 1 && (
            <div className="px-3 pt-3">
              <select
                className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background"
                value={accountId}
                onChange={(e) => { onSwitchAccount(e.target.value); setFolderSheetOpen(false); }}
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            </div>
          )}
          <ScrollArea className="flex-1">
            <nav className="p-2">
              <FolderTree
                nodes={folderTree}
                selectedId={selectedFolderId}
                onSelect={selectFolder}
                depth={0}
              />
            </nav>
          </ScrollArea>
          <div className="p-3 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-xs"
              onClick={() => { syncAccount.mutate({ id: accountId }); setFolderSheetOpen(false); }}
              disabled={syncAccount.isPending}
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${syncAccount.isPending ? "animate-spin" : ""}`} />
              {t("mail_page.sync_btn")}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Composer ── */}
      <Sheet open={composerOpen} onOpenChange={setComposerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl p-0 overflow-y-auto">
          <MailComposer
            account={activeAccount}
            initial={composerInitial}
            onClose={() => setComposerOpen(false)}
            onSent={() => {
              setComposerOpen(false);
              toast({ title: t("mail_page.sent_toast") });
              syncAccount.mutate({ id: accountId });
            }}
          />
        </SheetContent>
      </Sheet>

      {/* ── Reconnect modal ── */}
      {reconnectAccount && (
        <ReconnectMailModal
          account={reconnectAccount}
          open={reconnectOpen}
          onOpenChange={(v) => { setReconnectOpen(v); if (!v) setReconnectAccount(null); }}
          onReconnected={() => {
            const id = reconnectAccount?.id;
            if (id) syncAccount.mutate({ id });
            qc.invalidateQueries({ queryKey: getListMailAccountsQueryKey() });
            setReconnectAccount(null);
          }}
        />
      )}
    </div>
    </TooltipProvider>
  );
}

// ─── Folder Tree ──────────────────────────────────────────────────────────────

function FolderTree({
  nodes,
  selectedId,
  onSelect,
  depth,
  collapsed = false,
}: {
  nodes: FolderNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  depth: number;
  collapsed?: boolean;
}) {
  return (
    <>
      {nodes.map((node) => (
        <FolderTreeNode
          key={node.folder.id}
          node={node}
          selectedId={selectedId}
          onSelect={onSelect}
          depth={depth}
          collapsed={collapsed}
        />
      ))}
    </>
  );
}

function FolderTreeNode({
  node,
  selectedId,
  onSelect,
  depth,
  collapsed = false,
}: {
  node: FolderNode;
  selectedId: string | null;
  onSelect: (id: string) => void;
  depth: number;
  collapsed?: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(depth < 1);
  const { folder, children } = node;
  const active = selectedId === folder.id;
  const hasChildren = children.length > 0;

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`flex items-center justify-center w-8 h-8 mx-auto rounded-lg cursor-pointer mb-0.5
              ${active ? "bg-primary text-primary-foreground" : "hover:bg-accent/60 text-muted-foreground"}`}
            onClick={() => onSelect(folder.id)}
            data-testid={`folder-${folder.role}`}
          >
            <FolderIcon role={folder.role} className="w-4 h-4 shrink-0" />
            {folder.unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-primary" />
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">
          {folderDisplayName(folder, t)}
          {folder.unreadCount > 0 && ` (${folder.unreadCount})`}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <div>
      <div
        className={`group flex items-center gap-1 rounded-lg text-sm cursor-pointer select-none mb-0.5
          ${active ? "bg-primary text-primary-foreground" : "hover:bg-accent/60 text-foreground/80"}
        `}
        style={{ paddingLeft: `${0.5 + depth * 1}rem`, paddingRight: "0.5rem" }}
        onClick={() => onSelect(folder.id)}
        data-testid={`folder-${folder.role}`}
      >
        {/* Expand toggle */}
        <span
          className="w-4 h-4 flex items-center justify-center shrink-0"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
        >
          {hasChildren ? (
            expanded
              ? <ChevronDown className="w-3 h-3" />
              : <ChevronRight className="w-3 h-3" />
          ) : null}
        </span>

        <FolderIcon role={folder.role} className={`w-4 h-4 shrink-0 ${active ? "" : "text-muted-foreground"}`} />

        <span className="flex-1 truncate py-1.5">{folderDisplayName(folder, t)}</span>

        {folder.unreadCount > 0 && (
          <Badge
            variant={active ? "secondary" : "default"}
            className="h-4 text-[10px] px-1 ml-1 shrink-0"
          >
            {folder.unreadCount}
          </Badge>
        )}
      </div>

      {hasChildren && expanded && (
        <FolderTree
          nodes={children}
          selectedId={selectedId}
          onSelect={onSelect}
          depth={depth + 1}
          collapsed={false}
        />
      )}
    </div>
  );
}

// ─── Message Row ──────────────────────────────────────────────────────────────

function MessageRow({
  message,
  active,
  onClick,
  onFlag,
  onArchive,
  onDelete,
  onMarkRead,
  onMarkUnread,
  isRevealedByParent,
  onSwipeOpen,
}: {
  message: MailMessage;
  active: boolean;
  onClick: () => void;
  onFlag?: () => void;
  onArchive?: () => void;
  onDelete?: () => void;
  onMarkRead?: () => void;
  onMarkUnread?: () => void;
  isRevealedByParent: boolean;
  onSwipeOpen: () => void;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const initial = senderInitial(message.fromName, message.fromAddress);
  const unread = message.isUnread;
  const isNoise = message.triageCategory === "noise";

  // ── Swipe + reveal state ───────────────────────────────────────────────────
  const REVEAL_WIDTH = 130;
  const SWIPE_FLAG_THRESHOLD = 72;
  const SWIPE_OPEN_THRESHOLD = 60;

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isHorizontal = useRef(false);
  const [offset, setOffset] = useState(0);
  const [snapping, setSnapping] = useState(false);
  const [revealed, setRevealed] = useState(false);

  // ── Long-press state ───────────────────────────────────────────────────────
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);
  const [actionSheetOpen, setActionSheetOpen] = useState(false);

  const clearLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const snapTo = (x: number, open: boolean) => {
    setSnapping(true);
    setOffset(x);
    setRevealed(open);
  };
  const snapClosed = () => snapTo(0, false);
  const snapOpen = () => { snapTo(-REVEAL_WIDTH, true); onSwipeOpen(); };

  // Close this row whenever a different row becomes the active swipe row
  useEffect(() => {
    if (!isRevealedByParent && revealed) {
      setSnapping(true);
      setOffset(0);
      setRevealed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevealedByParent]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isHorizontal.current = false;
    longPressFired.current = false;
    if (!revealed) {
      setSnapping(false);
      longPressTimer.current = setTimeout(() => {
        longPressFired.current = true;
        setActionSheetOpen(true);
      }, 500);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (!isHorizontal.current) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      if (Math.abs(dy) > Math.abs(dx)) return;
      isHorizontal.current = true;
    }
    clearLongPress();
    if (revealed) {
      // Allow dragging back from revealed position
      const fromRevealed = -REVEAL_WIDTH + dx;
      setSnapping(false);
      setOffset(Math.min(0, Math.max(-REVEAL_WIDTH, fromRevealed)));
      return;
    }
    const maxRight = onFlag ? SWIPE_FLAG_THRESHOLD + 24 : 0;
    const maxLeft = -(onDelete || onArchive ? REVEAL_WIDTH + 40 : 0);
    setOffset(Math.max(maxLeft, Math.min(maxRight, dx)));
  };

  const handleTouchEnd = () => {
    clearLongPress();
    if (!isHorizontal.current) return;
    if (revealed) {
      if (offset > -SWIPE_OPEN_THRESHOLD) snapClosed();
      else snapOpen();
      return;
    }
    if (offset > SWIPE_FLAG_THRESHOLD && onFlag) {
      onFlag();
      snapClosed();
    } else if (offset < -SWIPE_OPEN_THRESHOLD && (onArchive || onDelete)) {
      snapOpen();
    } else {
      snapClosed();
    }
  };

  const handleContentClick = (e: React.MouseEvent) => {
    if (revealed) { e.preventDefault(); snapClosed(); return; }
    if (longPressFired.current) { longPressFired.current = false; return; }
    if (Math.abs(offset) > 5) { e.preventDefault(); snapClosed(); return; }
    onClick();
  };

  const handleRevealedAction = (e: React.MouseEvent, cb?: () => void) => {
    e.stopPropagation();
    snapClosed();
    cb?.();
  };

  const absOff = Math.abs(offset);
  const swipingRight = offset > 0;
  const effectiveOffset = revealed ? -REVEAL_WIDTH : offset;
  const showDragHint = !revealed && absOff > 4;

  return (
    <li className="relative overflow-hidden" data-testid={`mail-row-${message.id}`}>
      {/* Drag hint background (only while actively swiping, not when revealed) */}
      {showDragHint && (
        <div
          className={`absolute inset-0 flex items-center px-5 ${swipingRight ? "bg-amber-500" : "bg-blue-500"}`}
          style={{ opacity: Math.min(absOff / 50, 1) }}
          aria-hidden
        >
          {swipingRight
            ? <Star className="w-5 h-5 text-white" />
            : <Archive className="w-5 h-5 text-white ml-auto" />}
        </div>
      )}

      {/* Persistent action buttons revealed on swipe-left (always mounted for smooth animation) */}
      {/* md:hidden — swipe actions are mobile-only; desktop uses toolbar buttons in MailReader */}
      <div
        className="absolute inset-y-0 right-0 flex md:hidden"
        style={{ width: REVEAL_WIDTH }}
        aria-hidden={!revealed}
      >
        {onArchive && (
          <button
            className="flex-1 flex flex-col items-center justify-center gap-1 bg-blue-500 active:bg-blue-700 text-white text-[11px] font-semibold select-none"
            onClick={(e) => handleRevealedAction(e, onArchive)}
          >
            <Archive className="w-5 h-5" />
            <span>{t("mail_page.archive_btn")}</span>
          </button>
        )}
        {onDelete && (
          <button
            className="flex-1 flex flex-col items-center justify-center gap-1 bg-destructive active:opacity-80 text-destructive-foreground text-[11px] font-semibold select-none"
            onClick={(e) => handleRevealedAction(e, onDelete)}
          >
            <Trash2 className="w-5 h-5" />
            <span>{t("mail_page.move_to_trash_btn")}</span>
          </button>
        )}
      </div>

      {/* Row content — translates on swipe. Outer div MUST be fully opaque (bg-background)
          so the action buttons behind never show through. Opacity goes on the inner wrapper only. */}
      <div
        className={`cursor-pointer bg-background w-full
          ${active ? "bg-primary/8 border-l-2 border-l-primary" : "hover:bg-accent/40 border-l-2 border-l-transparent"}`}
        style={{
          transform: `translateX(${effectiveOffset}px)`,
          transition: snapping ? "transform 0.25s cubic-bezier(0.4,0,0.2,1)" : "none",
          willChange: "transform",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleContentClick}
      >
        {/* Inner wrapper: opacity applied here only, keeping outer bg fully opaque */}
        <div className={`flex items-start gap-3 px-3 py-3 w-full
          ${unread && !isNoise ? "" : "opacity-80"}
          ${isNoise ? "opacity-55" : ""}`}
        >
        {/* Avatar */}
        <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm font-medium
          ${unread && !isNoise ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
        >
          {initial}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-0.5">
            <span className={`text-sm truncate ${unread && !isNoise ? "font-semibold text-foreground" : "text-foreground/70"}`}>
              {message.fromName || message.fromAddress || t("mail_page.unknown_sender")}
            </span>
            <div className="flex items-center gap-1 shrink-0">
              {message.hasAttachments && <Paperclip className="w-3 h-3 text-muted-foreground" />}
              {message.isFlagged && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
              {unread && !isNoise && <span className="w-2 h-2 rounded-full bg-primary" />}
              <span
                className={`text-xs tabular-nums whitespace-nowrap ${
                  unread && !isNoise
                    ? "font-semibold text-foreground"
                    : "text-muted-foreground"
                }`}
                data-testid={`mail-row-date-${message.id}`}
              >
                {formatMsgDate(message.receivedAt, dateLocale, t("mail_page.yesterday"))}
              </span>
            </div>
          </div>
          <p className={`text-sm truncate ${unread && !isNoise ? "font-medium text-foreground" : "text-foreground/70"}`}>
            {message.subject || t("mail_page.no_subject")}
          </p>
          {message.snippet && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{message.snippet}</p>
          )}
          {(message.triageCategory || message.triageDeferredAt || message.triageProjectId || message.triageProjectHint || message.relatedContactId || (message.linkedTaskCount ?? 0) > 0) && (
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {message.triageCategory && <TriageBadge category={message.triageCategory} short />}
              {message.triageDeferredAt && !message.triageCategory && (
                <span
                  className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-900"
                  data-testid="badge-triage-deferred"
                >
                  {t("mail_page.deferred_badge")}
                </span>
              )}
              {(message.triageProjectId || message.triageProjectHint) && (
                message.triageProjectId ? (
                  <Link
                    href={`/app/projects/${message.triageProjectId}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[10px] text-primary/70 hover:text-primary truncate flex items-center gap-0.5 transition-colors"
                  >
                    <span>·</span>
                    <span className="truncate">{message.triageProjectHint ?? t("mail_page.linked_project")}</span>
                  </Link>
                ) : (
                  <span className="text-[10px] text-muted-foreground truncate">
                    · {message.triageProjectHint}
                  </span>
                )
              )}
              {message.relatedContactId && message.relatedContactName && (
                <Link
                  href={`/app/crm/${message.relatedContactId}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground/80 hover:text-primary transition-colors truncate"
                >
                  <span>·</span>
                  <span className="truncate">{message.relatedContactName}</span>
                  {message.relatedContactStatus && (
                    <span className="ml-0.5 px-1 py-0 rounded text-[9px] font-semibold uppercase tracking-wide bg-muted text-muted-foreground">
                      {t(`app_nav.contact_${message.relatedContactStatus}`, message.relatedContactStatus)}
                    </span>
                  )}
                </Link>
              )}
              {(message.linkedTaskCount ?? 0) > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground/70">
                  <span>·</span>
                  <span>{t("mail_page.linked_tasks", { count: message.linkedTaskCount })}</span>
                </span>
              )}
            </div>
          )}
        </div>
        </div>{/* end inner opacity wrapper */}
      </div>

      {/* ── Long-press action sheet ── */}
      <Sheet open={actionSheetOpen} onOpenChange={setActionSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-8">
          <SheetHeader className="pb-3 border-b border-border mb-2">
            <SheetTitle className="text-sm font-semibold text-left truncate">
              {message.fromName || message.fromAddress || t("mail_page.unknown_sender")}
            </SheetTitle>
            <p className="text-xs text-muted-foreground text-left truncate">
              {message.subject || t("mail_page.no_subject")}
            </p>
          </SheetHeader>
          <div className="space-y-0.5">
            {onFlag && (
              <button
                className="w-full flex items-center gap-3 px-2 py-3 rounded-xl text-left hover:bg-accent active:bg-accent/70"
                onClick={() => { setActionSheetOpen(false); onFlag(); }}
              >
                <Star className={`w-5 h-5 shrink-0 ${message.isFlagged ? "text-amber-500 fill-amber-500" : "text-muted-foreground"}`} />
                <span className="text-sm font-medium">
                  {message.isFlagged ? t("mail_page.unflag_btn") : t("mail_page.flag_btn")}
                </span>
              </button>
            )}
            {unread && onMarkRead && (
              <button
                className="w-full flex items-center gap-3 px-2 py-3 rounded-xl text-left hover:bg-accent active:bg-accent/70"
                onClick={() => { setActionSheetOpen(false); onMarkRead(); }}
              >
                <EyeOff className="w-5 h-5 shrink-0 text-muted-foreground" />
                <span className="text-sm font-medium">{t("mail_page.mark_read_btn")}</span>
              </button>
            )}
            {!unread && onMarkUnread && (
              <button
                className="w-full flex items-center gap-3 px-2 py-3 rounded-xl text-left hover:bg-accent active:bg-accent/70"
                onClick={() => { setActionSheetOpen(false); onMarkUnread(); }}
              >
                <EyeOff className="w-5 h-5 shrink-0 text-muted-foreground" />
                <span className="text-sm font-medium">{t("mail_page.mark_unread_btn")}</span>
              </button>
            )}
            {onArchive && (
              <button
                className="w-full flex items-center gap-3 px-2 py-3 rounded-xl text-left hover:bg-accent active:bg-accent/70"
                onClick={() => { setActionSheetOpen(false); onArchive(); }}
              >
                <Archive className="w-5 h-5 shrink-0 text-blue-500" />
                <span className="text-sm font-medium">{t("mail_page.archive_btn")}</span>
              </button>
            )}
            {onDelete && (
              <button
                className="w-full flex items-center gap-3 px-2 py-3 rounded-xl text-left hover:bg-accent active:bg-accent/70"
                onClick={() => { setActionSheetOpen(false); onDelete(); }}
              >
                <Trash2 className="w-5 h-5 shrink-0 text-destructive" />
                <span className="text-sm font-medium text-destructive">{t("mail_page.move_to_trash_btn")}</span>
              </button>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </li>
  );
}

// ─── Mail Reader ──────────────────────────────────────────────────────────────

// ─── AI analysis result panel ────────────────────────────────────────────────

const AI_TYPE_CONFIG: Record<
  string,
  { i18nKey: string; icon: typeof Zap; className: string }
> = {
  task: { i18nKey: "mail_page.ai_type_task", icon: Zap, className: "text-blue-600 bg-blue-50 border-blue-200" },
  reminder: { i18nKey: "mail_page.ai_type_reminder", icon: Bell, className: "text-orange-600 bg-orange-50 border-orange-200" },
  note: { i18nKey: "mail_page.ai_type_note", icon: FileText, className: "text-blue-600 bg-blue-50 border-blue-200" },
  decision: { i18nKey: "mail_page.ai_type_decision", icon: HelpCircle, className: "text-purple-600 bg-purple-50 border-purple-200" },
  project: { i18nKey: "mail_page.ai_type_project", icon: Folder, className: "text-yellow-600 bg-yellow-50 border-yellow-200" },
  event: { i18nKey: "mail_page.ai_type_event", icon: CalendarIcon, className: "text-pink-600 bg-pink-50 border-pink-200" },
};

// Defensive: AI payloads come back as untyped JSON. Only use `title` when it
// is a real string — otherwise React will throw "Objects are not valid as a
// React child" if the model returns a structured value.
function safePayloadTitle(payload: unknown): string | null {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const maybe = (payload as Record<string, unknown>).title;
    if (typeof maybe === "string" && maybe.trim()) return maybe.trim();
  }
  return null;
}

function AiAnalysisPanel({
  result,
  onDismiss,
}: {
  result: InboxItemWithSuggestions;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const detectedType = result.aiDetectedType ?? "note";
  const typeConfig = AI_TYPE_CONFIG[detectedType] ?? AI_TYPE_CONFIG.note;

  // Group suggestions by type for compact display.
  const groups = useMemo(() => {
    const map = new Map<string, typeof result.suggestions>();
    for (const s of result.suggestions) {
      const arr = map.get(s.type) ?? [];
      arr.push(s);
      map.set(s.type, arr);
    }
    return Array.from(map.entries());
  }, [result.suggestions]);

  return (
    <div
      className="mb-5 rounded-xl border border-primary/20 bg-primary/5 overflow-hidden"
      data-testid="ai-analysis-panel"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-primary/10 bg-primary/5">
        <Sparkles className="w-4 h-4 text-primary shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wider text-primary">
          Analyse IA
        </span>
        <span
          className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm border ${typeConfig.className}`}
        >
          {t(typeConfig.i18nKey)}
        </span>
        {result.aiConfidence != null && (
          <span className="text-[10px] text-muted-foreground">
            {t("mail_page.confidence_pct", { pct: Math.round(result.aiConfidence * 100) })}
          </span>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 -my-1 -mr-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-primary/10 active:bg-primary/15 transition-colors"
          aria-label={t("mail_page.close_btn")}
          data-testid="button-dismiss-ai"
        >
          <X className="w-4 h-4" />
          <span className="hidden sm:inline">{t("mail_page.close_btn")}</span>
        </button>
      </div>
      <div className="px-4 py-3 space-y-3">
        {result.aiSummary && (
          <p className="text-sm text-foreground leading-relaxed">{result.aiSummary}</p>
        )}
        {groups.length > 0 ? (
          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {t("mail_page.extracted_actions_label", { count: result.suggestions.length })}
            </p>
            <ul className="space-y-1.5">
              {groups.map(([type, items]) => {
                const cfg = AI_TYPE_CONFIG[type] ?? AI_TYPE_CONFIG.note;
                const Icon = cfg.icon;
                return items.map((s) => {
                  const title =
                    safePayloadTitle(s.payload) ??
                    t(cfg.i18nKey);
                  return (
                    <li
                      key={s.id}
                      className="flex items-start gap-2 text-sm text-foreground"
                    >
                      <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${cfg.className.split(" ")[0]}`} />
                      <span className="flex-1">{title}</span>
                    </li>
                  );
                });
              })}
            </ul>
            <p className="text-[11px] text-muted-foreground pt-1">
              Retrouvez ces suggestions dans l'onglet{" "}
              <Link href="/app/today" className="text-primary hover:underline">
                Aujourd'hui
              </Link>{" "}
              ou{" "}
              <Link href="/app/inbox" className="text-primary hover:underline">
                Inbox
              </Link>
              .
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            {t("mail_page.analyze_no_action")}
          </p>
        )}
      </div>
    </div>
  );
}

function MailReader({
  messageId,
  account,
  folderList,
  onBack,
  onReply,
  onDelete,
  onMoveSuccess,
}: {
  messageId: string;
  account: MailAccount;
  folderList: MailFolder[];
  onBack: () => void;
  onReply: (init: {
    to?: string[];
    subject?: string;
    bodyText?: string;
    inReplyTo?: string | null;
    references?: string | null;
  }) => void;
  onDelete: () => void;
  onMoveSuccess: () => void;
}) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : fr;
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data, isLoading } = useGetMailMessage(messageId);
  const message = data as MailMessageWithBody | undefined;
  // Always load remote images by default — the user prefers seeing email
  // visuals over the privacy/tracking trade-off. The banner + toggle below
  // remain in the code (gated on `!showImages`) so it's trivial to flip back.
  const [showImages, setShowImages] = useState(true);

  const markRead = useMarkMailMessageRead();
  useEffect(() => {
    if (message?.isUnread) {
      markRead.mutate(
        { id: messageId, data: { isRead: true } },
        {
          onSuccess: () => {
            qc.invalidateQueries({ queryKey: getGetMailMessageQueryKey(messageId) });
            qc.invalidateQueries({ queryKey: getListMailFoldersQueryKey(account.id) });
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, message?.isUnread]);

  const flag = useFlagMailMessage();
  const del = useDeleteMailMessage();
  const analyze = useAnalyzeMailMessage();
  const triage = useTriageMailMessage();
  const correct = useCorrectMailMessageTriage();
  const moveMsg = useMoveMailMessage();
  const archiveFolder = folderList.find((f) => f.role === "archive") ?? null;
  const trashFolder = folderList.find((f) => f.role === "trash") ?? null;
  const { data: suggestionsData } = useListMailMessageSuggestions(messageId, {
    query: {
      enabled: Boolean(message?.triageCategory),
      queryKey: getListMailMessageSuggestionsQueryKey(messageId),
    },
  });
  const suggestions = (suggestionsData ?? []) as SuggestedAction[];
  const acceptSuggestion = useAcceptSuggestion();

  const handleAcceptSuggestion = useCallback(
    (s: SuggestedAction) => {
      acceptSuggestion.mutate(
        { id: s.id },
        {
          onSuccess: () => {
            toast({ title: t("mail_page.task_created_toast") });
            qc.invalidateQueries({
              queryKey: getListMailMessageSuggestionsQueryKey(messageId),
            });
            qc.invalidateQueries({ queryKey: getGetMailMessageQueryKey(messageId) });
          },
          onError: (err: Error) =>
            toast({
              title: t("mail_page.task_create_error"),
              description: err.message,
              variant: "destructive",
            }),
        },
      );
    },
    [acceptSuggestion, qc, messageId, toast],
  );

  const handleCorrect = useCallback(
    (category: TriageCategory, learnRule: boolean) => {
      const learnedRule =
        learnRule && message?.fromAddress
          ? { patternType: "sender" as const, pattern: message.fromAddress }
          : null;
      correct.mutate(
        { id: messageId, data: { category, learnedRule } },
        {
          onSuccess: () => {
            toast({
              title: t("mail_page.triage_corrected_toast"),
              description: learnRule
                ? t("mail_page.triage_learn_desc")
                : t("mail_page.triage_marked_desc", { label: triageLabel(category) }),
            });
            qc.invalidateQueries({ queryKey: getGetMailMessageQueryKey(messageId) });
            qc.invalidateQueries({
              queryKey: getListMailMessagesQueryKey(account.id),
            });
          },
          onError: (err: Error) =>
            toast({
              title: t("mail_page.triage_correct_error"),
              description: err.message,
              variant: "destructive",
            }),
        },
      );
    },
    [correct, messageId, message?.fromAddress, qc, account.id, toast],
  );

  const handleRetriage = useCallback(() => {
    triage.mutate(
      { id: messageId },
      {
        onSuccess: () => {
          toast({ title: t("mail_page.retriage_toast") });
          qc.invalidateQueries({ queryKey: getGetMailMessageQueryKey(messageId) });
          qc.invalidateQueries({
            queryKey: getListMailMessagesQueryKey(account.id),
          });
        },
        onError: (err: Error) =>
          toast({
            title: t("mail_page.retriage_error"),
            description: err.message,
            variant: "destructive",
          }),
      },
    );
  }, [triage, messageId, qc, account.id, toast]);

  // AI analysis result shown inline below the sender card. Reset whenever the
  // user opens a different message — the analysis belongs to a single email.
  const [aiResult, setAiResult] = useState<InboxItemWithSuggestions | null>(null);
  useEffect(() => {
    setAiResult(null);
  }, [messageId]);

  // Track which messageId is currently selected so a late mutation response
  // for a previous email cannot overwrite the panel of the email the user is
  // now reading (race condition: click analyze on A → navigate to B → A's
  // response arrives → must be ignored).
  const currentMessageIdRef = useRef(messageId);
  useEffect(() => {
    currentMessageIdRef.current = messageId;
  }, [messageId]);

  const handleAnalyze = useCallback(() => {
    const requestedMessageId = messageId;
    analyze.mutate(
      { id: requestedMessageId },
      {
        onSuccess: (result) => {
          // Drop the result if the user has navigated to a different email.
          if (currentMessageIdRef.current !== requestedMessageId) return;
          setAiResult(result);
          // Refresh the global suggestion feed so the assistant panel and
          // unread/inbox counts pick up the new pending suggestions.
          qc.invalidateQueries({ queryKey: getListSuggestionsQueryKey() });
          qc.invalidateQueries({ queryKey: getListInboxQueryKey() });
          toast({
            title: t("mail_page.analyze_done_toast"),
            description:
              result.suggestions.length > 0
                ? t("mail_page.analyze_suggestions_desc", { count: result.suggestions.length })
                : t("mail_page.analyze_no_action"),
          });
        },
        onError: (err: Error) => {
          if (currentMessageIdRef.current !== requestedMessageId) return;
          toast({
            title: t("mail_page.analyze_error"),
            description: err.message,
            variant: "destructive",
          });
        },
      },
    );
  }, [analyze, messageId, qc, toast]);

  const handleMarkUnread = useCallback(() => {
    markRead.mutate(
      { id: messageId, data: { isRead: false } },
      {
        onSuccess: () => {
          toast({ title: t("mail_page.marked_unread_toast") });
          qc.invalidateQueries({ queryKey: getGetMailMessageQueryKey(messageId) });
          qc.invalidateQueries({ queryKey: getListMailFoldersQueryKey(account.id) });
          onBack();
        },
        onError: (err: Error) =>
          toast({ title: t("mail_page.mark_unread_error"), description: err.message, variant: "destructive" }),
      },
    );
  }, [markRead, messageId, qc, account.id, toast, onBack]);

  const handleArchive = useCallback(() => {
    if (!archiveFolder) {
      toast({ title: t("mail_page.no_archive_folder"), variant: "destructive" });
      return;
    }
    moveMsg.mutate(
      { id: messageId, data: { folderId: archiveFolder.id } },
      {
        onSuccess: () => {
          toast({ title: t("mail_page.archived_toast") });
          onMoveSuccess();
        },
        onError: (err: Error) =>
          toast({ title: t("mail_page.archive_error"), description: err.message, variant: "destructive" }),
      },
    );
  }, [moveMsg, messageId, archiveFolder, toast, onMoveSuccess]);

  const handleMoveToTrash = useCallback(() => {
    if (!trashFolder) {
      toast({ title: t("mail_page.no_trash_folder"), variant: "destructive" });
      return;
    }
    moveMsg.mutate(
      { id: messageId, data: { folderId: trashFolder.id } },
      {
        onSuccess: () => {
          toast({ title: t("mail_page.moved_to_trash_toast") });
          onMoveSuccess();
        },
        onError: (err: Error) =>
          toast({ title: t("mail_page.move_trash_error"), description: err.message, variant: "destructive" }),
      },
    );
  }, [moveMsg, messageId, trashFolder, toast, onMoveSuccess]);

  const handleReply = useCallback(() => {
    if (!message) return;
    onReply({
      to: message.fromAddress ? [message.fromAddress] : [],
      subject: message.subject?.startsWith("Re:") ? message.subject : `Re: ${message.subject ?? ""}`,
      bodyText: `\n\n---\n${
        message.receivedAt
          ? format(new Date(message.receivedAt), i18n.language === "en" ? "d MMM yyyy 'at' HH:mm" : "d MMM yyyy 'à' HH:mm", { locale: dateLocale })
          : ""
      }, ${message.fromName ?? message.fromAddress ?? ""} wrote:\n${
        message.bodyText
          ? message.bodyText.split("\n").map((l) => `> ${l}`).join("\n")
          : ""
      }`,
      inReplyTo: message.messageId ?? null,
      references: [message.references, message.messageId].filter(Boolean).join(" ") || null,
    });
  }, [message, onReply]);

  if (isLoading || !message) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-64 w-full mt-6" />
      </div>
    );
  }

  const html = message.bodySanitizedHtml ?? null;
  const renderedHtml = html
    ? showImages
      ? DOMPurify.sanitize(
          html.replace(/data-original-src=/gi, "src=").replace(/data-original-srcset=/gi, "srcset="),
          { ADD_ATTR: ["target", "rel"] },
        )
      : DOMPurify.sanitize(html, { ADD_ATTR: ["target", "rel"] })
    : null;
  const hasRemoteImages = Boolean(html && /data-original-src=/i.test(html));

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-border shrink-0">
        {/* Retour — toujours visible pour désélectionner le mail courant */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={onBack}
          title={t("mail_page.back_btn")}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1" />
        {/* Actions principales */}
        <Button variant="ghost" size="sm" onClick={handleReply} data-testid="button-reply" className="gap-1.5">
          <Reply className="w-4 h-4" /> <span className="hidden sm:inline">{t("mail_page.reply_btn")}</span>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleArchive}
          disabled={moveMsg.isPending || !archiveFolder}
          data-testid="button-archive"
          title={t("mail_page.archive_btn")}
        >
          <Archive className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={handleMoveToTrash}
          disabled={moveMsg.isPending || !trashFolder}
          data-testid="button-trash"
          title={t("mail_page.move_to_trash_btn")}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
        {/* Favoris / Drapeau — directement dans la toolbar */}
        <Button
          variant="ghost"
          size="icon"
          className={`h-8 w-8 ${message.isFlagged ? "text-amber-500" : ""}`}
          onClick={() =>
            flag.mutate(
              { id: messageId, data: { isFlagged: !message.isFlagged } },
              { onSuccess: () => qc.invalidateQueries({ queryKey: getGetMailMessageQueryKey(messageId) }) },
            )
          }
          data-testid="button-flag"
          title={message.isFlagged ? t("mail_page.unflag_btn") : t("mail_page.flag_btn")}
        >
          {message.isFlagged ? <Star className="w-4 h-4 fill-current" /> : <Star className="w-4 h-4" />}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="button-more-actions">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={handleMarkUnread} data-testid="button-mark-unread">
              <EyeOff className="w-4 h-4 mr-2" /> {t("mail_page.mark_unread_btn")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleAnalyze}
              disabled={analyze.isPending}
              data-testid="button-analyze"
            >
              {analyze.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {aiResult ? t("mail_page.re_analyze_title") : t("mail_page.analyze_title")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() =>
                del.mutate(
                  { id: messageId },
                  {
                    onSuccess: () => { toast({ title: t("mail_page.delete_toast") }); onDelete(); },
                    onError: (err: Error) =>
                      toast({ title: t("mail_page.delete_error"), description: err.message, variant: "destructive" }),
                  },
                )
              }
              data-testid="button-delete"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {t("mail_page.delete_permanent_btn")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Message content */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="px-4 sm:px-6 py-5 max-w-3xl mx-auto overflow-hidden">
          {/* Subject */}
          <h1 className="text-lg font-semibold mb-4 leading-snug">{message.subject || t("mail_page.no_subject")}</h1>

          {/* Sender card */}
          <div className="flex items-start gap-3 mb-5 p-3 rounded-xl bg-muted/40 border border-border">
            <div className="w-10 h-10 rounded-full bg-primary/20 text-primary flex items-center justify-center font-semibold shrink-0">
              {senderInitial(message.fromName, message.fromAddress)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="font-medium text-sm">{message.fromName ?? message.fromAddress}</span>
                {message.fromName && (
                  <span className="text-xs text-muted-foreground truncate">&lt;{message.fromAddress}&gt;</span>
                )}
              </div>
              {message.toAddresses && message.toAddresses.length > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t("mail_page.to_label")} {message.toAddresses.map((a) => a.name ?? a.address).join(", ")}
                </p>
              )}
              {message.receivedAt && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {format(new Date(message.receivedAt), i18n.language === "en" ? "d MMM yyyy 'at' HH:mm" : "d MMM yyyy 'à' HH:mm", { locale: dateLocale })}
                </p>
              )}
            </div>
          </div>

          {/* AI analysis result (inline) */}
          {aiResult && (
            <AiAnalysisPanel
              result={aiResult}
              onDismiss={() => setAiResult(null)}
            />
          )}

          {/* Lecture IA */}
          {message.triageCategory ? (
            <div className="mb-5 rounded-xl border border-border bg-muted/30 p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                  <Sparkles className="w-4 h-4 text-primary shrink-0" />
                  <span className="text-sm font-semibold">{t("mail_page.reading_label")}</span>
                  <TriageBadge category={message.triageCategory} />
                  {typeof message.triageConfidence === "number" && (
                    (message.triageConfidence ?? 0) >= 0.8 ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] uppercase tracking-wide font-semibold bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-200 dark:border-blue-900"
                      >
                        <ShieldCheck className="w-3 h-3 mr-1" /> {t("mail_page.confirmed_badge")}
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-[10px] uppercase tracking-wide font-semibold bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-900"
                      >
                        <HelpCircle className="w-3 h-3 mr-1" /> {t("mail_page.to_confirm_badge")}
                      </Badge>
                    )
                  )}
                  {message.triageSource && (
                    <span className="text-[11px] text-muted-foreground">
                      {t("mail_page.source_label", { source: message.triageSource })}
                    </span>
                  )}
                </div>
                <div className="shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-7" data-testid="button-correct-triage">
                      {t("mail_page.correct_btn")}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel>{t("mail_page.reclassify_label")}</DropdownMenuLabel>
                    {TRIAGE_CATEGORIES.map((c) => (
                      <DropdownMenuItem
                        key={c}
                        disabled={c === message.triageCategory}
                        onClick={() => handleCorrect(c, false)}
                      >
                        {triageLabel(c)}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                      {t("mail_page.learn_rule_label")}
                    </DropdownMenuLabel>
                    {message.fromAddress ? (
                      <>
                        <DropdownMenuItem onClick={() => handleCorrect("noise", true)}>
                          {t("mail_page.always_noise_label")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCorrect("fyi", true)}>
                          {t("mail_page.always_fyi_label")}
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <DropdownMenuItem disabled>
                        {t("mail_page.unknown_sender_label")}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleRetriage}
                      disabled={triage.isPending}
                    >
                      {t("mail_page.reassess_ai_label")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                </div>
              </div>
              {message.triageSummary && (
                <p className="text-sm text-foreground/90 leading-relaxed">
                  {message.triageSummary}
                </p>
              )}
              {message.triageProjectId ? (
                <Link
                  href={`/projects/${message.triageProjectId}`}
                  className="text-xs text-primary mt-2 inline-flex items-center gap-1 hover:underline"
                  data-testid="link-triage-project"
                >
                  <ExternalLink className="w-3 h-3" /> {t("mail_page.view_in_project_label")}
                </Link>
              ) : message.triageProjectHint ? (
                <p className="text-xs text-muted-foreground mt-1">
                  {t("mail_page.probable_project_label", { name: message.triageProjectHint })}
                </p>
              ) : null}

              {/* Suggested tasks for action_required */}
              {message.triageCategory === "action_required" && suggestions.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">
                    {t("mail_page.suggested_tasks_label")}
                  </p>
                  <ul className="space-y-1.5">
                    {suggestions.map((s) => {
                      const payload = (s.payload ?? {}) as {
                        title?: string;
                        description?: string;
                      };
                      return (
                        <li
                          key={s.id}
                          className="flex items-start justify-between gap-2 p-2 rounded-md border border-border bg-background"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium line-clamp-2">
                              {payload.title ?? t("mail_page.ai_type_task")}
                            </p>
                            {payload.description && (
                              <p className="text-xs text-muted-foreground line-clamp-2">
                                {payload.description}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0"
                            onClick={() => handleAcceptSuggestion(s)}
                            disabled={acceptSuggestion.isPending}
                            data-testid={`button-create-task-${s.id}`}
                          >
                            <CheckSquare className="w-3.5 h-3.5 mr-1 sm:mr-1" />
                            <span className="hidden sm:inline">{t("mail_page.create_task_btn")}</span>
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          ) : message.triageDeferredAt ? (
            <div
              className="mb-5 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-3 flex items-center justify-between gap-3"
              data-testid="panel-triage-deferred"
            >
              <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200 flex-1 min-w-0">
                <Sparkles className="w-4 h-4 shrink-0" />
                <span className="leading-snug">
                  {t("mail_page.deferred_badge")} — {t("mail_page.ai_cap_reached_short")}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={handleRetriage}
                disabled={triage.isPending}
              >
                {triage.isPending ? t("mail_page.reading_btn") : t("mail_page.retry_btn")}
              </Button>
            </div>
          ) : (
            <div className="mb-5 rounded-xl border border-dashed border-border p-3 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground flex-1 min-w-0">
                <Sparkles className="w-4 h-4 shrink-0" />
                <span>{t("mail_page.not_read_ai")}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={handleRetriage}
                disabled={triage.isPending}
              >
                {triage.isPending ? t("mail_page.reading_btn") : t("mail_page.read_ai_btn")}
              </Button>
            </div>
          )}

          {/* Remote images banner */}
          {hasRemoteImages && !showImages && (
            <div className="mb-4 flex items-center gap-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-sm">
              <span className="flex-1 text-amber-800">{t("mail_page.remote_images_blocked")}</span>
              <Button variant="outline" size="sm" onClick={() => setShowImages(true)}>
                {t("mail_page.show_images_btn")}
              </Button>
            </div>
          )}

          {/* Body */}
          {renderedHtml ? (
            <div
              className="prose prose-sm max-w-none text-foreground overflow-x-auto"
              dangerouslySetInnerHTML={{ __html: renderedHtml }}
            />
          ) : (
            <pre className="whitespace-pre-wrap font-sans text-sm text-foreground/90 leading-relaxed">
              {message.bodyText || t("mail_page.empty_body")}
            </pre>
          )}

          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-6 pt-4 border-t border-border">
              <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" />
                {t("mail_page.attachments_label")} ({message.attachments.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {message.attachments.map((att: { id: string; filename?: string | null; contentType?: string | null; sizeBytes?: number | null; contentId?: string | null }) => {
                  const name = att.filename ?? "fichier";
                  const sizeKb = att.sizeBytes ? Math.ceil(att.sizeBytes / 1024) : null;
                  return (
                    <a
                      key={att.id}
                      href={`${API_BASE}/api/mail/attachments/${att.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-muted/40 hover:bg-muted/70 transition-colors text-sm max-w-xs group"
                    >
                      <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="truncate min-w-0 flex-1">{name}</span>
                      {sizeKb && (
                        <span className="text-xs text-muted-foreground shrink-0">{sizeKb} Ko</span>
                      )}
                      <Download className="w-3.5 h-3.5 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Quick reply strip */}
      <div className="px-4 py-3 border-t border-border shrink-0">
        <button
          onClick={handleReply}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border border-border bg-muted/30 text-sm text-muted-foreground hover:bg-muted/60 transition-colors text-left"
        >
          <Reply className="w-4 h-4 shrink-0" />
          {t("mail_page.reply_to_label", { sender: message.fromName ?? message.fromAddress })}
        </button>
      </div>
    </div>
  );
}
